import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, LogIn, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth, formatApiErrorDetail } from '@/context/AuthContext';
import { toast } from 'sonner';

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect') || '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      toast.success('¡Bienvenido!', { description: 'Sesión iniciada correctamente' });
      navigate(redirect);
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] bg-gradient-to-br from-[#F7F2E8] via-[#FDFBF7] to-[#F7F2E8] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl border border-[#E7E5E4] p-8 shadow-sm">
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-full bg-[#2A5C43] flex items-center justify-center mx-auto mb-4">
              <LogIn className="w-6 h-6 text-white" />
            </div>
            <p className="text-xs tracking-[0.25em] uppercase text-[#D47A4A] font-semibold mb-2">Bienvenido</p>
            <h1 className="font-display font-bold text-3xl text-[#1C1917] tracking-tight">Inicia sesión</h1>
            <p className="text-sm text-[#57534E] mt-2">Ingresa a tu cuenta de Nutripetgourmet</p>
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-[#1C1917] font-medium">Correo electrónico</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8BA58F]" />
                <Input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-11 rounded-xl border-[#E7E5E4] focus-visible:ring-[#2A5C43]"
                  placeholder="tu@correo.com"
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-[#1C1917] font-medium">Contraseña</Label>
                <Link to="/recuperar-contrasena" className="text-xs text-[#D47A4A] hover:underline" data-testid="login-forgot-link">
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8BA58F]" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 h-11 rounded-xl border-[#E7E5E4] focus-visible:ring-[#2A5C43]"
                  placeholder="••••••••"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8BA58F] hover:text-[#2A5C43]"
                  data-testid="login-toggle-password"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#2A5C43] hover:bg-[#214A35] text-white rounded-full font-semibold"
              data-testid="login-submit"
            >
              {loading ? 'Ingresando...' : (
                <>Iniciar sesión <ArrowRight className="w-4 h-4 ml-2" /></>
              )}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-[#E7E5E4] text-center">
            <p className="text-sm text-[#57534E]">
              ¿No tienes cuenta?{' '}
              <Link to={`/registro${redirect !== '/' ? `?redirect=${redirect}` : ''}`} className="text-[#2A5C43] font-semibold hover:underline" data-testid="login-register-link">
                Regístrate aquí
              </Link>
            </p>
          </div>
        </div>

        <p className="text-center text-xs text-[#57534E] mt-6">
          Al iniciar sesión aceptas nuestros <Link to="/terminos" className="underline">términos</Link> y <Link to="/privacidad" className="underline">política de privacidad</Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
